import React, { useEffect, useState } from 'react';
import { ArrowLeft, Edit2, X, ZoomIn, ZoomOut, RotateCw, RotateCcw, Minus, Plus, Download, Move, Loader2, Package, Globe } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import FirebaseProductService from '../../../../services/product.service';
import { auth, onAuthStateChanged } from '../../../../firebase/config';

// Advanced Image Preview Modal
const ImagePreviewModal = ({ image, productName, onClose }) => {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 5));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.1));
  };

  const handleRotateLeft = () => {
    setRotation(prev => prev - 90);
  };

  const handleRotateRight = () => {
    setRotation(prev => prev + 90);
  };

  const handleReset = () => {
    setScale(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (scale > 1) {
      setIsDragging(true);
      setStartPos({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - startPos.x,
        y: e.clientY - startPos.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-default"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-6xl max-h-[90vh]">
        {/* Modal Header */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <h3 className="text-white font-medium text-lg truncate max-w-md">
              {productName}
            </h3>
            <span className="text-gray-300 text-sm bg-black/30 px-3 py-1 rounded-full">
              {Math.round(scale * 100)}%
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Image Container */}
        <div className="relative h-[70vh] bg-gradient-to-br from-gray-900 to-black rounded-xl overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center">
            <img
              src={image}
              alt={productName}
              className={`transition-transform duration-200 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale}) rotate(${rotation}deg)`,
                maxWidth: '90%',
                maxHeight: '90%'
              }}
              onMouseDown={handleMouseDown}
            />
          </div>
          
          {/* Drag Instruction */}
          {scale > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/60 text-white text-sm px-4 py-2 rounded-full flex items-center gap-2">
              <Move className="w-4 h-4" />
              <span>Drag to pan image</span>
            </div>
          )}
        </div>

        {/* Control Panel */}
        <div className="mt-6 bg-gray-900/80 backdrop-blur-sm rounded-xl p-4 border border-gray-700">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Zoom Controls */}
            <div className="flex items-center gap-2">
              <span className="text-gray-300 text-sm font-medium">Zoom:</span>
              <button
                onClick={handleZoomOut}
                disabled={scale <= 0.1}
                className="p-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Minus className="w-5 h-5" />
              </button>
              <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-orange-500"
                  style={{ width: `${((scale - 0.1) / (5 - 0.1)) * 100}%` }}
                />
              </div>
              <button
                onClick={handleZoomIn}
                disabled={scale >= 5}
                className="p-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>

            {/* Rotate Controls */}
            <div className="flex items-center gap-2">
              <span className="text-gray-300 text-sm font-medium">Rotate:</span>
              <button
                onClick={handleRotateLeft}
                className="p-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                <RotateCcw className="w-5 h-5" />
              </button>
              <button
                onClick={handleRotateRight}
                className="p-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                <RotateCw className="w-5 h-5" />
              </button>
            </div>

            {/* Action Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleReset}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <span>Reset</span>
              </button>
              <a
                href={image}
                download={`${productName.replace(/\s+/g, '_')}_image.jpg`}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                <span>Download</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ViewProductPage = () => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams();

  // Helper function to get image URL from product data
  const getImageUrl = (imageData) => {
    if (!imageData) return null;
    
    // If imageData is a string (URL or base64)
    if (typeof imageData === 'string') {
      // Check if it's a base64 data URL
      if (imageData.startsWith('data:image')) {
        return imageData;
      }
      
      // Check if it's a regular URL
      if (imageData.startsWith('http://') || imageData.startsWith('https://')) {
        return imageData;
      }
    }
    
    // If imageData is an object (from Firestore)
    if (typeof imageData === 'object' && imageData !== null) {
      // Check if it has a data property
      if (imageData.data) {
        const dataValue = imageData.data;
        
        // If data is a string
        if (typeof dataValue === 'string') {
          if (dataValue.startsWith('data:image') || 
              dataValue.startsWith('http://') || 
              dataValue.startsWith('https://')) {
            return dataValue;
          }
        }
      }
      
      // Check if it has a url property
      if (imageData.url) {
        const urlValue = imageData.url;
        if (typeof urlValue === 'string' && 
            (urlValue.startsWith('http://') || urlValue.startsWith('https://'))) {
          return urlValue;
        }
      }
      
      // For placeholder images
      if (imageData.name && imageData.name.includes('placeholder')) {
        return `https://via.placeholder.com/400x400?text=${encodeURIComponent(imageData.name)}`;
      }
    }
    
    // Return null if no valid image found
    return null;
  };

  // Check authentication and load product
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setAuthUser(user);
        await loadProduct();
      } else {
        toast.error('Please login to view product details');
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, []);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const data = await FirebaseProductService.getProductById(id);
      
      if (data) {
        console.log('Product data loaded:', data);
        console.log('Images data:', data.images);
        
        // Extract image URLs from the product data
        const imageUrls = [];
        if (data.images && Array.isArray(data.images)) {
          data.images.forEach((img, index) => {
            const url = getImageUrl(img);
            if (url) {
              imageUrls.push(url);
            } else {
              console.warn(`Could not extract URL for image ${index}:`, img);
            }
          });
        }
        
        console.log('Extracted image URLs:', imageUrls);
        
        // Transform the data to match the expected format
        const transformedData = {
          id: data.id,
          name: data.name || 'Unnamed Product',
          category: data.category || 'Uncategorized',
          hscode: data.hsCode || 'N/A',
          description: data.description || 'No description available',
          specification: Array.isArray(data.specifications) 
            ? data.specifications.join(' | ') 
            : data.specifications || 'No specifications provided',
          quantity: data.quantity || 0,
          unit: data.unit || 'PCS',
          price: data.price || 0,
          currency: data.currency || 'INR',
          status: data.status || 'DRAFT',
          images: imageUrls, // Use extracted image URLs
          createdAt: data.createdAt || new Date(),
          updatedAt: data.updatedAt || new Date(),
          // Store raw data for reference
          rawImages: data.images || [],
          // Include other important fields
          sellerId: data.sellerId,
          sellerName: data.sellerName || 'Unknown Seller',
          views: data.views || 0,
          favorites: data.favorites || 0,
          isActive: data.isActive !== undefined ? data.isActive : true
        };
        
        setProduct(transformedData);
      } else {
        toast.error('Product not found');
        navigate('/seller/myproducts');
      }
    } catch (error) {
      console.error('Failed to load product:', error);
      toast.error(error.message || 'Failed to load product');
      navigate('/seller/myproducts');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/seller/myproducts');
  };

  const handleEdit = () => {
    if (product) {
      navigate(`/seller/myproducts/${product.id}/edit`);
    }
  };

  const handlePreviewImage = (image) => {
    setPreviewImage(image);
    setShowPreview(true);
  };

  const handleDownloadImage = (image) => {
    if (image) {
      const link = document.createElement('a');
      link.href = image;
      link.download = `${product.name.replace(/\s+/g, '_')}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Format price with currency symbol
  const formatPrice = (price, currency) => {
    const priceNum = parseFloat(price) || 0;
    
    // Define currency symbols
    const currencySymbols = {
      'INR': '₹',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'CNY': '¥',
      'JPY': '¥',
      'AUD': 'A$',
      'CAD': 'C$'
    };
    
    const symbol = currencySymbols[currency] || currency;
    
    // Format number with commas for thousands
    const formattedPrice = priceNum.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    return `${symbol} ${formattedPrice}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
          <p className="text-lg font-semibold text-gray-900">Loading product...</p>
        </div>
      </div>
    );
  }

  if (!authUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Authentication Required</h2>
          <p className="text-gray-600 mb-4">Please login to view product details</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Product Not Found</h2>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-10 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            <span className="text-lg font-medium">Product Details</span>
          </button>
        </div>

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Column - Image Section */}
          <div className="lg:w-2/5">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-800">Product Images</h2>
                <p className="text-sm text-gray-500 mt-1">Click on image to preview with full controls</p>
              </div>

              {/* Main Image Container */}
              <div className="relative group mb-4">
                <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg overflow-hidden border border-gray-200">
                  {product.images && product.images.length > 0 && product.images[0] ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 cursor-zoom-in"
                      onClick={() => handlePreviewImage(product.images[0])}
                      onError={(e) => {
                        console.error('Failed to load image:', e.target.src);
                        e.target.src = 'https://via.placeholder.com/600x600?text=Image+Not+Available';
                        e.target.onerror = null; // Prevent infinite loop
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                          <X className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-gray-500">No image available</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Hover Overlay */}
                  {product.images && product.images.length > 0 && product.images[0] && (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                      <button
                        onClick={() => handlePreviewImage(product.images[0])}
                        className="opacity-0 group-hover:opacity-100 transform group-hover:scale-100 scale-90 transition-all duration-300 px-6 py-3 bg-white text-gray-900 font-medium rounded-lg shadow-lg hover:shadow-xl hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-2">
                          <ZoomIn className="w-5 h-5" />
                          <span>Preview Image</span>
                        </div>
                      </button>
                    </div>
                  )}
                </div>

                {/* Image Info */}
                {product.images && product.images.length > 0 && product.images[0] && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Main Product Image</p>
                        <p className="text-xs text-gray-500">High Resolution</p>
                      </div>
                      <button
                        onClick={() => handlePreviewImage(product.images[0])}
                        className="px-4 py-2 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors flex items-center gap-2"
                      >
                        <ZoomIn className="w-4 h-4" />
                        <span>Preview</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                {product.images && product.images.length > 0 && product.images[0] && (
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    <button 
                      onClick={() => handleDownloadImage(product.images[0])}
                      className="p-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors text-sm text-gray-700 flex items-center justify-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Additional Images */}
              {product.images && product.images.length > 1 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    Additional Images ({product.images.slice(1).filter(img => img).length})
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    {product.images.slice(1).map((image, index) => (
                      image && (
                        <div 
                          key={index} 
                          className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 cursor-pointer hover:border-orange-400 transition-colors"
                        >
                          <img
                            src={image}
                            alt={`${product.name} - Image ${index + 2}`}
                            className="w-full h-full object-cover"
                            onClick={() => handlePreviewImage(image)}
                            onError={(e) => {
                              console.error('Failed to load additional image:', e.target.src);
                              e.target.src = 'https://via.placeholder.com/200x200?text=Image+Error';
                              e.target.onerror = null; // Prevent infinite loop
                            }}
                          />
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Product Details */}
          <div className="lg:w-3/5">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">{product.name}</h1>
                <p className="text-gray-600 mt-2">View and manage product information</p>
              </div>

              {/* Product Details Grid */}
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <span className="text-gray-800">{product.category}</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">HS Code</label>
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <span className="text-gray-800">{product.hscode}</span>
                    </div>
                  </div>
                </div>

                {/* Quantity and Price Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Package className="w-4 h-4 text-gray-500" />
                      Quantity
                    </label>
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-bold text-gray-900">
                          {product.quantity.toLocaleString()}
                        </span>
                        <span className="px-3 py-1 bg-gray-200 text-gray-700 text-sm font-medium rounded-full">
                          {product.unit}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">Total available units</p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price
                    </label>
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-bold text-gray-900">
                          {formatPrice(product.price, product.currency)}
                        </span>
                        <span className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
                          <Globe className="w-3 h-3" />
                          {product.currency}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">Price per {product.unit.toLowerCase()}</p>
                    </div>
                  </div>
                </div>

                {/* Unit Price Calculation */}
                {product.quantity > 0 && product.price > 0 && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-800">Total Value</p>
                        <p className="text-xs text-blue-600 mt-1">Based on current quantity and price</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-blue-900">
                          {formatPrice(product.quantity * product.price, product.currency)}
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                          {product.quantity.toLocaleString()} {product.unit} × {formatPrice(product.price, product.currency)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-gray-800 leading-relaxed whitespace-pre-line">
                      {product.description}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Specifications</label>
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg mb-6">
                    <div className="space-y-2">
                      {product.specification.split(' | ').map((spec, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <span className="text-orange-500 mt-1">•</span>
                          <span className="text-gray-800">{spec.trim()}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Status and Timestamps */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Current Status</p>
                        <p className="text-xs text-gray-500 mt-1">Product approval status</p>
                      </div>
                      <span className={`px-4 py-2 text-sm font-medium rounded-full ${
                        product.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                        product.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                        product.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {product.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <p className="text-xs text-gray-500">Created</p>
                        <p className="text-sm font-medium text-gray-800">
                          {product.createdAt instanceof Date 
                            ? `${product.createdAt.toLocaleDateString()} ${product.createdAt.toLocaleTimeString()}`
                            : new Date(product.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <p className="text-xs text-gray-500">Last Updated</p>
                        <p className="text-sm font-medium text-gray-800">
                          {product.updatedAt instanceof Date 
                            ? `${product.updatedAt.toLocaleDateString()} ${product.updatedAt.toLocaleTimeString()}`
                            : new Date(product.updatedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex justify-end gap-3">
                  <button
                    onClick={handleBack}
                    className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Back to Products
                  </button>
                  <button
                    onClick={handleEdit}
                    className="px-6 py-2.5 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Edit2 className="w-5 h-5" />
                    <span>Edit Product</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {showPreview && previewImage && (
        <ImagePreviewModal
          image={previewImage}
          productName={product.name}
          onClose={() => {
            setShowPreview(false);
            setPreviewImage(null);
          }}
        />
      )}
    </div>
  );
};

export default ViewProductPage;